import { getToolName, isToolUIPart, type UIDataTypes, type UIMessagePart, type UITools } from 'ai';

import type { UIMessagePartsLike } from './data-suggestions';
import { toolProgressCompletedLabel, toolProgressLabel } from './tool-progress-labels';

export type ToolProgressStatus = 'active' | 'completed' | 'failed';

export type ToolProgressItem = {
  toolCallId: string;
  toolName: string;
  label: string;
  completedLabel: string;
  status: ToolProgressStatus;
};

function statusFromToolState(state: string | undefined): ToolProgressStatus {
  if (state === 'output-available') return 'completed';
  if (state === 'output-error' || state === 'output-denied') return 'failed';
  return 'active';
}

/**
 * Build customer-safe progress rows from AI SDK tool parts.
 * Agent SSE sets `dynamic: true`, so live streams usually use `type: 'dynamic-tool'`.
 */
export function getToolProgressItems(message: UIMessagePartsLike): ToolProgressItem[] {
  const parts = message.parts;
  if (!parts?.length) {
    return [];
  }

  const byId = new Map<string, ToolProgressItem>();
  const order: string[] = [];

  for (const raw of parts) {
    if (typeof (raw as { type?: unknown }).type !== 'string') {
      continue;
    }
    const part = raw as UIMessagePart<UIDataTypes, UITools>;
    if (!isToolUIPart(part)) {
      continue;
    }
    const toolCallId = typeof part.toolCallId === 'string' ? part.toolCallId : '';
    if (!toolCallId) {
      continue;
    }
    const toolName = getToolName(part);
    if (typeof toolName !== 'string' || !toolName.trim()) {
      continue;
    }
    const status = statusFromToolState(part.state);
    const next: ToolProgressItem = {
      toolCallId,
      toolName,
      label: toolProgressLabel(toolName),
      completedLabel: toolProgressCompletedLabel(toolName),
      status,
    };
    if (!byId.has(toolCallId)) {
      order.push(toolCallId);
    }
    byId.set(toolCallId, next);
  }

  return order.map(id => byId.get(id)!);
}

export function messageHasToolProgress(message: UIMessagePartsLike): boolean {
  return getToolProgressItems(message).length > 0;
}

export function messageHasActiveOrFailedToolProgress(message: UIMessagePartsLike): boolean {
  return getToolProgressItems(message).some(item => item.status === 'active' || item.status === 'failed');
}
